import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  Crown, Check, X, Sparkles, Zap, Shield, Star, Wallet,
  ArrowRight, Tag, Loader2, CheckCircle, AlertCircle, RefreshCw
} from 'lucide-react';

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [togglingAutoRenew, setTogglingAutoRenew] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState(false);
  const [validatingPromo, setValidatingPromo] = useState(false);
  
  // Subscribe dialog
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    // Auto-fill promo code from QR flyer redirect
    const pendingPromo = localStorage.getItem('pending_promo_code');
    if (pendingPromo) {
      setPromoCode(pendingPromo);
      localStorage.removeItem('pending_promo_code');
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('clinic_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [plansRes, subRes, walletRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/plans`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/my-subscription`, { headers }).catch(() => ({ data: null })),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/balance`, { headers }).catch(() => ({ data: { balance: 0 } }))
      ]);
      
      // API returns {plans: [...]} so extract the array
      // Normalize plan fields to handle both old (price) and new (base_price) schema
      const rawPlans = plansRes.data?.plans || plansRes.data || [];
      const normalizedPlans = rawPlans.map(plan => ({
        ...plan,
        base_price: plan.base_price ?? plan.price ?? 0,
        billing_cycle: plan.billing_cycle || 'monthly'
      }));
      setPlans(normalizedPlans);
      // Extract subscription from API response {has_subscription, subscription}
      setCurrentSubscription(subRes.data?.subscription || null);
      setWalletBalance(walletRes.data?.balance || 0);
    } catch (err) {
      console.error('Failed to fetch subscription data', err);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    setValidatingPromo(true);
    try {
      const token = localStorage.getItem('clinic_token');
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/promo-codes/validate?code=${encodeURIComponent(promoCode)}&plan_id=${selectedPlan?.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPromoDiscount(res.data.discount_amount || 0);
      setPromoApplied(true);
      toast.success(`Promo code applied! You save ₹${res.data.discount_amount}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
      setPromoDiscount(0);
      setPromoApplied(false);
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    
    const finalPrice = Math.max(0, selectedPlan.base_price - promoDiscount);
    
    if (walletBalance < finalPrice) {
      toast.error(`Insufficient balance. Please add ₹${(finalPrice - walletBalance).toFixed(0)} to your wallet.`);
      return;
    }
    
    setSubscribing(true);
    try {
      const token = localStorage.getItem('clinic_token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/subscribe`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            plan_id: selectedPlan.id,
            promo_code: promoApplied ? promoCode : null
          }
        }
      );
      toast.success(`Successfully subscribed to ${selectedPlan.name}!`);
      setShowSubscribeDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const openSubscribeDialog = (plan) => {
    setSelectedPlan(plan);
    setPromoCode('');
    setPromoDiscount(0);
    setPromoApplied(false);
    setShowSubscribeDialog(true);
  };

  const toggleAutoRenew = async (enable) => {
    setTogglingAutoRenew(true);
    try {
      const token = localStorage.getItem('clinic_token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/toggle-auto-renew?enable=${enable}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentSubscription(prev => ({ ...prev, auto_renew: enable }));
      toast.success(`Auto-renewal ${enable ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update auto-renewal setting');
    } finally {
      setTogglingAutoRenew(false);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'basic': return <Zap className="w-6 h-6" />;
      case 'advance': return <Sparkles className="w-6 h-6" />;
      case 'advance_plus': return <Crown className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'basic': return 'from-slate-500 to-slate-600';
      case 'advance': return 'from-amber-500 to-orange-600';
      case 'advance_plus': return 'from-purple-500 to-indigo-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'basic': return 'bg-slate-100 text-slate-700';
      case 'advance': return 'bg-amber-100 text-amber-700';
      case 'advance_plus': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const featuresList = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'appointments', label: 'Appointment Management' },
    { key: 'patients', label: 'Patient Records' },
    { key: 'clinical', label: 'Clinical Visits' },
    { key: 'billing', label: 'Billing & Invoicing' },
    { key: 'ai_scribe', label: 'AI Clinical Scribe' },
    { key: 'marketplace', label: 'B2B Marketplace Access' },
    { key: 'inventory', label: 'Inventory Management' },
    { key: 'wallet', label: 'Wallet & Payments' },
    { key: 'expenses', label: 'Expense Tracking' },
    { key: 'mr_visitors', label: 'MR Visitor Management' },
    { key: 'patient_history', label: 'Patient History' },
    { key: 'analytics', label: 'Advanced Analytics' },
    { key: 'online_consultation', label: 'Online Video Consultation' },
    { key: 'face_vitals', label: 'Face Vitals Scanner' },
    { key: 'marketing_growth', label: 'Marketing & Growth' },
    { key: 'vendor_bills', label: 'Vendor Bills (B2B Billing Visibility)' },
    { key: 'whatsapp_reminders', label: 'WhatsApp Reminders' },
    { key: 'priority_support', label: 'Priority Support' },
    { key: 'custom_branding', label: 'Custom Branding' }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Choose Your Plan</h1>
          <p className="text-slate-600">Unlock premium features to grow your practice</p>
          
          {/* Wallet Balance */}
          <div className="mt-4 inline-flex items-center gap-2 bg-teal-50 px-4 py-2 rounded-full">
            <Wallet className="w-5 h-5 text-teal-600" />
            <span className="text-teal-800 font-medium">Wallet Balance: ₹{(walletBalance || 0).toLocaleString()}</span>
            <Button size="sm" variant="outline" onClick={() => navigate('/doctor/wallet')} className="ml-2 h-7">
              Add Funds
            </Button>
          </div>
        </div>

        {/* Current Subscription */}
        {currentSubscription && (
          <Card className={`mb-8 ${currentSubscription.status === 'expired' ? 'border-red-200 bg-red-50/50' : 'border-teal-200 bg-teal-50/50'}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getTierColor(currentSubscription.tier)} flex items-center justify-center text-white`}>
                    {getTierIcon(currentSubscription.tier)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{currentSubscription.status === 'expired' ? 'Expired' : 'Current'} Plan: {currentSubscription.plan_name}</p>
                    <p className="text-sm text-slate-600">
                      {currentSubscription.status === 'expired' ? 'Expired' : 'Expires'}: {new Date(currentSubscription.expires_at?.endsWith?.('Z') ? currentSubscription.expires_at : currentSubscription.expires_at + 'Z').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {currentSubscription.status !== 'expired' && (
                    <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border">
                      <RefreshCw className={`w-4 h-4 ${currentSubscription.auto_renew ? 'text-teal-600' : 'text-slate-400'}`} />
                      <Label htmlFor="auto-renew" className="text-sm text-slate-700 cursor-pointer">
                        Auto-Renew
                      </Label>
                      <Switch
                        id="auto-renew"
                        checked={currentSubscription.auto_renew || false}
                        onCheckedChange={toggleAutoRenew}
                        disabled={togglingAutoRenew}
                      />
                    </div>
                  )}
                  <Badge className={currentSubscription.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}>
                    {currentSubscription.status === 'expired' ? 'Expired' : 'Active'}
                  </Badge>
                </div>
              </div>
              {currentSubscription.status !== 'expired' && currentSubscription.auto_renew && (
                <p className="text-xs text-teal-700 mt-3 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Your subscription will auto-renew using wallet balance
                </p>
              )}
              {currentSubscription.status === 'expired' && currentSubscription.renewal_failure_reason && (
                <p className="text-xs text-red-600 mt-3 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Auto-renewal failed: {currentSubscription.renewal_failure_reason}. Please subscribe manually or add funds to your wallet.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.length === 0 ? (
            <div className="col-span-3 text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No subscription plans available</p>
            </div>
          ) : plans.map((plan) => {
            const isCurrentPlan = currentSubscription?.plan_id === plan.id && currentSubscription?.status === 'active';
            const isPopular = plan.tier === 'advance';
            
            return (
              <Card 
                key={plan.id} 
                className={`relative overflow-hidden transition-all hover:shadow-lg ${
                  isPopular ? 'border-2 border-blue-400 shadow-blue-100' : ''
                } ${isCurrentPlan ? 'ring-2 ring-teal-400' : ''}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    Most Popular
                  </div>
                )}
                
                {/* Header */}
                <CardHeader className={`bg-gradient-to-br ${getTierColor(plan.tier)} text-white pb-8`}>
                  <div className="flex items-center gap-2 mb-2">
                    {getTierIcon(plan.tier)}
                    <Badge className="bg-white/20 text-white capitalize">{plan.tier.replace('_', ' ')}</Badge>
                  </div>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-white/80">{plan.description}</CardDescription>
                </CardHeader>
                
                {/* Price */}
                <div className="px-6 -mt-4">
                  <div className="bg-white rounded-lg shadow-md p-4 text-center">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl font-bold text-slate-800">₹{(plan.base_price || 0).toLocaleString()}</span>
                      <span className="text-slate-500">/{plan.billing_cycle === 'monthly' ? 'mo' : 'yr'}</span>
                    </div>
                  </div>
                </div>
                
                {/* Features */}
                <CardContent className="pt-6">
                  <p className="text-sm font-medium text-slate-700 mb-3">Features included:</p>
                  <ul className="space-y-2">
                    {featuresList.map((feature) => {
                      const hasFeature = plan.features?.[feature.key];
                      return (
                        <li key={feature.key} className={`flex items-center gap-2 text-sm ${hasFeature ? 'text-slate-700' : 'text-slate-400'}`}>
                          {hasFeature ? (
                            <Check className="w-4 h-4 text-teal-500 flex-shrink-0" />
                          ) : (
                            <X className="w-4 h-4 text-slate-300 flex-shrink-0" />
                          )}
                          <span className={!hasFeature ? 'line-through' : ''}>{feature.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
                
                {/* Footer */}
                <CardFooter className="pt-0">
                  {isCurrentPlan ? (
                    <Button disabled className="w-full bg-teal-100 text-teal-700 hover:bg-teal-100">
                      <CheckCircle className="w-4 h-4 mr-2" /> Current Plan
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => openSubscribeDialog(plan)}
                      className={`w-full bg-gradient-to-r ${getTierColor(plan.tier)} hover:opacity-90`}
                    >
                      Subscribe Now <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Subscribe Dialog */}
        <Dialog open={showSubscribeDialog} onOpenChange={setShowSubscribeDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Subscribe to {selectedPlan?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Plan Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Plan</span>
                  <span className="font-medium">{selectedPlan?.name}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Billing Cycle</span>
                  <span className="font-medium capitalize">{selectedPlan?.billing_cycle}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Price</span>
                  <span className="font-medium">₹{(selectedPlan?.base_price || 0).toLocaleString()}</span>
                </div>
                {promoApplied && promoDiscount > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span>Promo Discount</span>
                    <span>-₹{(promoDiscount || 0).toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t mt-2 pt-2 flex justify-between items-center font-bold">
                  <span>Total</span>
                  <span className="text-lg">₹{Math.max(0, (selectedPlan?.base_price || 0) - promoDiscount).toLocaleString()}</span>
                </div>
              </div>
              
              {/* Promo Code */}
              <div>
                <Label className="text-sm text-slate-600">Have a promo code?</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    disabled={promoApplied}
                    className="uppercase"
                  />
                  <Button 
                    variant="outline" 
                    onClick={validatePromoCode}
                    disabled={validatingPromo || promoApplied || !promoCode}
                  >
                    {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                  </Button>
                </div>
                {promoApplied && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Promo code applied!
                  </p>
                )}
              </div>
              
              {/* Wallet Balance */}
              <div className="flex items-center justify-between p-3 bg-teal-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-teal-600" />
                  <span className="text-slate-700">Wallet Balance</span>
                </div>
                <span className={`font-bold ${(walletBalance || 0) >= Math.max(0, (selectedPlan?.base_price || 0) - promoDiscount) ? 'text-teal-600' : 'text-red-600'}`}>
                  ₹{(walletBalance || 0).toLocaleString()}
                </span>
              </div>
              
              {walletBalance < Math.max(0, (selectedPlan?.base_price || 0) - promoDiscount) && (
                <div className="flex items-center gap-2 text-amber-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>Insufficient balance. Please add funds to your wallet.</span>
                </div>
              )}
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSubscribeDialog(false)}>Cancel</Button>
              {walletBalance < Math.max(0, (selectedPlan?.base_price || 0) - promoDiscount) ? (
                <Button onClick={() => { setShowSubscribeDialog(false); navigate('/doctor/wallet'); }}>
                  <Wallet className="w-4 h-4 mr-2" /> Add Funds
                </Button>
              ) : (
                <Button 
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {subscribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Confirm & Pay
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
